import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Checkbox from '@repo/ui/checkbox'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getTripFolderDocuments } from '../../../api/trip-folder/get-trip-folder-documents'
import {
  TripFolderPdfFormInput,
  TripFolderPdfFormPaxList
} from '../../../lib/types/models/trip-folder/trip-folder'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createNumberArraySchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledGroupedCheckboxInput } from '../../elements/inputs/controlled-grouped-checkbox-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<TripFolderPdfFormInput> = z.object({
  cases: z.boolean().optional(),
  tickets: z.boolean().optional(),
  listOfPax: z
    .object({
      reservations: z.boolean().optional(),
      centers: z.boolean().optional()
    })
    .optional(),
  documentIds: createNumberArraySchema('documents', { isInteger: true, optional: true }).optional()
})

export function TripFolderPdfForm({
  tripFolderId,
  className,
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<TripFolderPdfFormInput, true> & { tripFolderId: number }) {
  const form = useForm<TripFolderPdfFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const paxListAttributes: Record<keyof TripFolderPdfFormPaxList, string> = {
    reservations: 'Par réservation',
    centers: 'Par centres'
  }

  const { data: documents } = useQuery({
    initialData: [],
    queryKey: ['trip-folder-documents', tripFolderId],
    queryFn: () => getTripFolderDocuments(tripFolderId)
  })

  return (
    <Form form={form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className={className}
      >
        <div className={'flex flex-col gap-2 p-4'}>
          <Typography variant={'label-medium'} color={tailwindColors.grey.lighter}>
            Sélection des onglets et des options d'impression :
          </Typography>

          <FormField
            control={form.control}
            name={'cases'}
            render={({ field }) => (
              <FormItem className={'border w-fit rounded-sm p-2 border-grey-light'}>
                <FormControl>
                  <Checkbox {...field} label={'Dossier'} checked={field.value} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* List of Pax */}
          <FormField
            control={form.control}
            name={'listOfPax'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledGroupedCheckboxInput<TripFolderPdfFormPaxList>
                    {...field}
                    label={'Liste(s) de passagers'}
                    options={paxListAttributes}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Attached Documents */}
          {documents && documents.length > 0 && (
            <FormField
              control={form.control}
              name={'documentIds'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledGroupedCheckboxInput<Record<string, boolean>>
                      {...field}
                      label={'Documents annexes'}
                      options={
                        documents.reduce<Record<string, string>>(
                          (acc, document) => ({
                            ...acc,
                            [document.id.toString()]: document.name
                          }),
                          {}
                        ) || {}
                      }
                      value={
                        documents.reduce<Record<string, boolean>>(
                          (acc, document) => ({
                            ...acc,
                            [document.id.toString()]: field.value?.includes(document.id) || false
                          }),
                          {}
                        ) || {}
                      }
                      onChange={(object) =>
                        field.onChange(
                          Object.entries(object)
                            .filter(([, checked]) => checked)
                            .map(([key]) => Number(key))
                        )
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name={'tickets'}
            render={({ field }) => (
              <FormItem className={'border w-fit rounded-sm p-2 border-grey-light'}>
                <FormControl>
                  <Checkbox {...field} label={'Billets'} checked={field.value} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="pb-6 px-4 border-t border-grey-light">
          <ValidationButtons {...validationButtonProps} />
        </div>
      </form>
    </Form>
  )
}
